import type { DocumentPayload } from '@Application/Beneficiaries/Documents/Actions/documentsFetched';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AbstractListView from '@UI/Store/AbstractListView';
import DocumentView from '@UI/Store/Documents/Views/DocumentView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * `typeKey` and `subjectId` scope this list: `stopOn`
 * compares them (`isFor`) against the dossier on screen to tell a freshly fetched list apart from a
 * previously visited one. The "Consulter" link of a row comes from `WorkflowRunDocumentView.documentId`
 * on the workflow run — never a lookup by `documentType` in this list.
 */
class DocumentsListView extends AbstractListView<DocumentView> {
	readonly typeKey: string;
	readonly subjectId: number;
	readonly totalRecords: number;

	constructor(list: readonly DocumentView[], typeKey: string, subjectId: number, totalRecords: number) {
		super(list, 'DocumentsListView');

		this.typeKey = typeKey;
		this.subjectId = subjectId;
		this.totalRecords = totalRecords;

		Object.freeze(this);
	}

	isFor(subject: SubjectAddress): boolean {
		return this.typeKey === subject.typeKey && this.subjectId === subject.subjectId;
	}

	/** Returns a new list with the row of the same `id` swapped for `view`, order kept; the same
	 * instance when no row carries that `id`. */
	replace(view: DocumentView): DocumentsListView {
		if (this.findById(view.id) === null) {
			return this;
		}

		return new DocumentsListView(
			this.list.map((item) => (item.id === view.id ? view : item)),
			this.typeKey,
			this.subjectId,
			this.totalRecords,
		);
	}

	/** Replaces the row of the same `id`; a row the list does not hold goes first, as the list is
	 * ordered by creation date descending. */
	upsert(view: DocumentView): DocumentsListView {
		if (this.findById(view.id) !== null) {
			return this.replace(view);
		}

		return new DocumentsListView(
			[view, ...this.list],
			this.typeKey,
			this.subjectId,
			this.totalRecords + 1,
		);
	}

	/** Same instance when no row carries that `id`, so a replayed deletion changes nothing. */
	remove(documentId: number): DocumentsListView {
		if (this.findById(documentId) === null) {
			return this;
		}

		return new DocumentsListView(
			this.list.filter((item) => item.id !== documentId),
			this.typeKey,
			this.subjectId,
			this.totalRecords - 1,
		);
	}

	static fromPayload(
		payload: { totalRecords: number; results: readonly DocumentPayload[] },
		typeKey: string,
		subjectId: number,
	): DocumentsListView {
		return new DocumentsListView(
			payload.results.map((documentPayload) => new DocumentView(documentPayload)),
			typeKey,
			subjectId,
			payload.totalRecords,
		);
	}
}

export default DocumentsListView;
